import { AUTH_ENDPOINT_PREFIX } from '@core/api/api-endpoints';
import { APP_CONFIG } from '@core/config/app-config';

/**
 * URL classification shared by the interceptors.
 *
 * Kept in its own file rather than in index.ts: that barrel exists solely to
 * publish the ordered interceptor list, and re-exporting helpers through it
 * would start it down the road of pulling unrelated modules into whichever
 * chunk touches it.
 */

/** '/api' in dev (proxied), absolute in prod. Trailing slash stripped once. */
const API_BASE = APP_CONFIG.apiUrl.replace(/\/+$/, '');

/**
 * True for our own API only.
 *
 * The bearer token must never ride along to a third-party host - a font CDN
 * or a map tile server would receive a valid credential in a request header
 * and log it.
 */
export function isApiUrl(url: string): boolean {
  return url === API_BASE || url.startsWith(`${API_BASE}/`) || url.startsWith(`${API_BASE}?`);
}

/** The `Controller/Action` part of an API URL, query string removed. */
export function endpointOf(url: string): string {
  if (!isApiUrl(url)) return '';
  const withoutBase = url.slice(API_BASE.length).replace(/^\/+/, '');
  const queryAt = withoutBase.indexOf('?');
  return queryAt === -1 ? withoutBase : withoutBase.slice(0, queryAt);
}

/**
 * True only for api/Auth/*.
 *
 * withCredentials is scoped to exactly this group. Sending the cookie
 * everywhere would give the ~99% of endpoints that mutate data an ambient
 * credential and hand back the CSRF surface that the bearer-header design
 * exists to remove.
 */
export function isAuthEndpointUrl(url: string): boolean {
  return endpointOf(url).startsWith(AUTH_ENDPOINT_PREFIX);
}
